import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    catchError,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/utils/api-response';
import { Vendor, VendorCategory } from './vendor.types';

@Injectable({ providedIn: 'root' })
export class VendorSharedService {
    // Base URLs
    private readonly BASE_URL = environment.apiUrl;
    private readonly VENDORS_URL = `${this.BASE_URL}/vendors`;
    private readonly VENDOR_CATEGORIES_URL = `${this.BASE_URL}/vendor_categories`;

    // BehaviorSubjects
    private _category: BehaviorSubject<VendorCategory | null> =
        new BehaviorSubject<VendorCategory | null>(null);
    private _categories: BehaviorSubject<VendorCategory[] | null> =
        new BehaviorSubject<VendorCategory[] | null>(null);
    private _vendor: BehaviorSubject<Vendor | null> =
        new BehaviorSubject<Vendor | null>(null);
    private _vendors: BehaviorSubject<Vendor[] | null> = new BehaviorSubject<
        Vendor[] | null
    >(null);

    constructor(private _httpClient: HttpClient) {}

    // Accessors
    get category$(): Observable<VendorCategory | null> {
        return this._category.asObservable();
    }

    get categories$(): Observable<VendorCategory[] | null> {
        return this._categories.asObservable();
    }

    get vendor$(): Observable<Vendor | null> {
        return this._vendor.asObservable();
    }

    get vendors$(): Observable<Vendor[] | null> {
        return this._vendors.asObservable();
    }

    get getVendorsUrl(): string {
        return this.VENDORS_URL;
    }

    updateVendors(vendors: Vendor[]): void {
        this._vendors.next(vendors);
    }

    // Category Methods
    getCategoriesBySchool(
        schoolCode: string
    ): Observable<ApiResponse<VendorCategory[]>> {
        return this._httpClient
            .get<ApiResponse<VendorCategory[]>>(
                `${this.VENDOR_CATEGORIES_URL}/school/${schoolCode}`
            )
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._categories.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch vendor categories:', error);
                    const errorResponse: ApiResponse<VendorCategory[]> =
                        error?.error || {
                            success: false,
                            message: 'Failed to fetch vendor categories',
                            data: null,
                            timestamp: new Date(),
                        };
                    return throwError(() => errorResponse);
                })
            );
    }

    createCategory(
        category: VendorCategory
    ): Observable<ApiResponse<VendorCategory>> {
        return this.categories$.pipe(
            take(1),
            switchMap((categories) =>
                this._httpClient
                    .post<ApiResponse<VendorCategory>>(
                        this.VENDOR_CATEGORIES_URL,
                        category
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                this._categories.next([
                                    response.data,
                                    ...(categories || []),
                                ]);
                            }
                        }),
                        catchError((error) => {
                            console.error(
                                'Failed to create vendor category:',
                                error
                            );
                            const errorResponse: ApiResponse<VendorCategory> =
                                error?.error || {
                                    success: false,
                                    message: 'Failed to create vendor category',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    updateCategory(
        category: VendorCategory
    ): Observable<ApiResponse<VendorCategory>> {
        return this.categories$.pipe(
            take(1),
            switchMap((categories) =>
                this._httpClient
                    .put<ApiResponse<VendorCategory>>(
                        `${this.VENDOR_CATEGORIES_URL}/code/${category.code}`,
                        category
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                const index =
                                    categories?.findIndex(
                                        (item) => item.code === category.code
                                    ) ?? -1;
                                if (index !== -1 && categories) {
                                    categories[index] = response.data;
                                    this._categories.next(categories);
                                }
                            }
                        }),
                        catchError((error) => {
                            console.error(
                                'Failed to update vendor category:',
                                error
                            );
                            const errorResponse: ApiResponse<VendorCategory> =
                                error?.error || {
                                    success: false,
                                    message: 'Failed to update vendor category',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    deleteCategory(code: string): Observable<ApiResponse<void>> {
        return this.categories$.pipe(
            take(1),
            switchMap((categories) =>
                this._httpClient
                    .delete<ApiResponse<void>>(
                        `${this.VENDOR_CATEGORIES_URL}/code/${code}`
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && categories) {
                                const filteredCategories = categories.filter(
                                    (item) => item.code !== code
                                );
                                this._categories.next(filteredCategories);
                            }
                        }),
                        catchError((error) => {
                            console.error(
                                'Failed to delete vendor category:',
                                error
                            );
                            const errorResponse: ApiResponse<void> =
                                error?.error || {
                                    success: false,
                                    message: 'Failed to delete vendor category',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    // Vendor Methods
    getVendorsBySchool(schoolCode: string): Observable<ApiResponse<Vendor[]>> {
        return this._httpClient
            .get<ApiResponse<Vendor[]>>(
                `${this.VENDORS_URL}/school/${schoolCode}`
            )
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._vendors.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch vendors:', error);
                    const errorResponse: ApiResponse<Vendor[]> =
                        error?.error || {
                            success: false,
                            message: 'Failed to fetch vendors',
                            data: null,
                            timestamp: new Date(),
                        };
                    return throwError(() => errorResponse);
                })
            );
    }

    createVendor(vendor: Vendor): Observable<ApiResponse<Vendor>> {
        return this.vendors$.pipe(
            take(1),
            switchMap((vendors) =>
                this._httpClient
                    .post<ApiResponse<Vendor>>(this.VENDORS_URL, vendor)
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                this._vendors.next([
                                    response.data,
                                    ...(vendors || []),
                                ]);
                            }
                        }),
                        catchError((error) => {
                            console.error('Failed to create vendor:', error);
                            const errorResponse: ApiResponse<Vendor> =
                                error?.error || {
                                    success: false,
                                    message: 'Failed to create vendor',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    updateVendor(vendor: Vendor): Observable<ApiResponse<Vendor>> {
        return this.vendors$.pipe(
            take(1),
            switchMap((vendors) =>
                this._httpClient
                    .put<ApiResponse<Vendor>>(
                        `${this.VENDORS_URL}/${vendor.code}`,
                        vendor
                    )
                    .pipe(
                        tap((response) => {
                            if (response.success && response.data) {
                                const index =
                                    vendors?.findIndex(
                                        (item) => item.code === vendor.code
                                    ) ?? -1;
                                if (index !== -1 && vendors) {
                                    vendors[index] = response.data;
                                    this._vendors.next(vendors);
                                }
                            }
                        }),
                        catchError((error) => {
                            console.error('Failed to update vendor:', error);
                            const errorResponse: ApiResponse<Vendor> =
                                error?.error || {
                                    success: false,
                                    message: 'Failed to update vendor',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    deleteVendor(code: string): Observable<ApiResponse<void>> {
        return this.vendors$.pipe(
            take(1),
            switchMap((vendors) =>
                this._httpClient
                    .delete<ApiResponse<void>>(`${this.VENDORS_URL}/${code}`)
                    .pipe(
                        tap((response) => {
                            if (response.success && vendors) {
                                const filteredVendors = vendors.filter(
                                    (item) => item.code !== code
                                );
                                this._vendors.next(filteredVendors);
                            }
                        }),
                        catchError((error) => {
                            console.error('Failed to delete vendor:', error);
                            const errorResponse: ApiResponse<void> =
                                error?.error || {
                                    success: false,
                                    message: 'Failed to delete vendor',
                                    data: null,
                                    timestamp: new Date(),
                                };
                            return throwError(() => errorResponse);
                        })
                    )
            )
        );
    }

    getVendorCategoriesPagedBySchool(
        schoolCode: string,
        page: number = 0,
        size: number = 10,
        search: string = ''
    ): Observable<
        ApiResponse<{ content: VendorCategory[]; totalElements: number }>
    > {
        let url = `${this.VENDOR_CATEGORIES_URL}/school/${schoolCode}/paged?page=${page}&size=${size}`;
        if (search) {
            url += `&search=${encodeURIComponent(search)}`;
        }

        return this._httpClient
            .get<
                ApiResponse<{
                    content: VendorCategory[];
                    totalElements: number;
                }>
            >(url)
            .pipe(
                tap((response) => {
                    // console.log('Fetched paged vendor categories:', response);
                }),
                catchError((error) => {
                    console.error(
                        'Failed to fetch paged vendor categories:',
                        error
                    );
                    const errorResponse: ApiResponse<{
                        content: VendorCategory[];
                        totalElements: number;
                    }> = error?.error || {
                        success: false,
                        message: 'Failed to fetch paged vendor categories',
                        data: null,
                        timestamp: new Date().toISOString(),
                    };
                    return throwError(() => errorResponse);
                })
            );
    }
}
